import { useEffect, useState } from 'react';
import { connexionsEquipeApi } from '../../api/resources';
import { Shell } from '../../components/layout/Shell.jsx';
import { localeDates, tr } from '../../lib/i18n';
import { C } from '../../theme/palette';

/** Icône, libellé et couleur de chaque rôle de l'équipe. */
const ROLES = {
  OUVRIER: ['👷', 'Ouvrier', C.amber],
  TECHNICIEN: ['⚙', 'Technicien', C.navy],
  COMPTABLE: ['∑', 'Comptable / Fiduciaire', C.teal],
  SECRETAIRE: ['✎', 'Secrétaire', C.purple],
};

const FUSEAU = { timeZone: 'Europe/Zurich' };

/** « Lundi 19 octobre 2026 », le jour d'une connexion, à l'heure de Suisse. */
export function jourDe(instant) {
  const d = new Date(instant);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(localeDates(), {
    weekday: 'long',
    day: '2-digit',
    month: 'long',
    year: 'numeric',
    ...FUSEAU,
  });
}

/** « 07:12 », l'heure d'une connexion, à l'heure de Suisse. */
export function heureDe(instant) {
  const d = new Date(instant);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString('fr-CH', { hour: '2-digit', minute: '2-digit', ...FUSEAU });
}

/**
 * Journal des connexions de l'équipe : chaque ouvrier, technicien, comptable
 * ou secrétaire qui se connecte, le jour et l'heure.
 *
 * <p>Le serveur note chaque ouverture de session, quel que soit le chemin
 * (mot de passe, code d'ouvrier, lien reçu par e-mail). L'ancien journal était
 * écrit par l'écran du collaborateur, qui n'en avait pas le droit : la page
 * restait vide, et les ouvriers n'y figuraient pas.
 *
 * @param {object} props propriétés de la page
 * @returns {JSX.Element} la page
 */
export function JournalConnexionsPage({ onNav, soc, estMixte, univers, setUnivers }) {
  const [liste, setListe] = useState(null);
  const [erreur, setErreur] = useState('');

  useEffect(() => {
    let vivant = true;
    Promise.resolve()
      .then(() => connexionsEquipeApi.liste())
      .then((l) => {
        if (vivant) setListe(Array.isArray(l) ? l : []);
      })
      .catch((e) => {
        if (vivant) setErreur(e?.message || 'La liste n’a pas pu être lue.');
      });
    return () => {
      vivant = false;
    };
  }, []);

  const parJour = {};
  (liste || []).forEach((c) => {
    const j = jourDe(c.connecteLe);
    (parJour[j] = parJour[j] || []).push(c);
  });
  const jours = Object.keys(parJour);

  return (
    <Shell
      active="plus"
      onNav={onNav}
      soc={soc}
      back
      onBack={() => onNav('plus')}
      estMixte={estMixte}
      univers={univers}
      setUnivers={setUnivers}
      pageTitle="🔐 Journal des connexions"
    >
      <div
        style={{
          background: C.navyXL,
          border: '1px solid ' + C.border,
          borderRadius: 11,
          padding: '11px 13px',
          marginBottom: 12,
        }}
      >
        <div style={{ fontSize: 10.5, color: C.ink, fontWeight: 700 }}>🔒 Réservé au patron</div>
        <div style={{ fontSize: 9, color: C.grey, marginTop: 3, lineHeight: 1.5 }}>
          Chaque connexion des ouvriers, techniciens, secrétaires et comptables, avec le jour et
          l’heure. Les treize derniers mois sont gardés.
        </div>
      </div>
      {erreur && (
        <div role="alert" style={{ fontSize: 11, color: C.red, marginBottom: 10 }}>
          {erreur}
        </div>
      )}
      {!erreur && liste === null && (
        <div role="status" style={{ textAlign: 'center', color: C.muted, fontSize: 11 }}>
          Chargement…
        </div>
      )}
      {liste && liste.length === 0 && (
        <div
          style={{
            textAlign: 'center',
            color: C.muted,
            padding: '44px 0',
            fontSize: 12,
            lineHeight: 1.6,
          }}
        >
          🔕 Aucune connexion enregistrée pour l’instant.
          <div style={{ fontSize: 9.5, marginTop: 6 }}>
            Chaque connexion d’un ouvrier, d’un technicien, de la comptable ou de la secrétaire
            s’inscrira ici.
          </div>
        </div>
      )}
      {jours.map((j) => (
        <div key={j} style={{ marginBottom: 14 }}>
          <div
            style={{
              fontSize: 9,
              fontWeight: 800,
              color: C.muted,
              letterSpacing: 0.3,
              marginBottom: 7,
            }}
          >
            {/* La majuscule au seul premier mot : « capitalize » l'écrivait
                aussi au mois, « Mercredi, 21 Octobre 2026 ». */}
            {String(j).charAt(0).toUpperCase() + String(j).slice(1)}
          </div>
          {parJour[j].map((c, i) => {
            const [ic, libelle, couleur] = ROLES[c.role] || ['👤', c.role, C.navy];
            return (
              <div
                key={c.connecteLe + '-' + i}
                data-testid="connexion"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 11,
                  background: C.white,
                  border: '1px solid ' + C.border,
                  borderLeft: '3px solid ' + couleur,
                  borderRadius: 10,
                  padding: '11px 13px',
                  marginBottom: 7,
                }}
              >
                <div
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: 9,
                    background: couleur + '16',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 15,
                    flexShrink: 0,
                  }}
                >
                  {ic}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 800, color: C.ink }}>{c.nom}</div>
                  <div style={{ fontSize: 9, color: C.muted, marginTop: 1 }}>{tr(libelle)}</div>
                </div>
                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                  <div
                    style={{
                      fontSize: 14,
                      fontWeight: 900,
                      color: couleur,
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {heureDe(c.connecteLe)}
                  </div>
                  <div style={{ fontSize: 8, color: C.muted }}>connexion</div>
                </div>
              </div>
            );
          })}
        </div>
      ))}
    </Shell>
  );
}
